import {Subject, map, takeUntil, filter, debounceTime, tap} from 'rxjs';
import {Directive, Input, OnDestroy, forwardRef, ElementRef, AfterViewInit} from '@angular/core';
import {VIRTUAL_SCROLL_STRATEGY} from '@angular/cdk/scrolling';
import {ResizeObservable} from "@shared/util/dom/resize.observable";
import {MutationObservable} from "@shared/util/dom/mutation.observable";
import {DynamicHeightVirtualScrollStrategy} from './dynamic-height-virtual-scroll.strategy';

@Directive({
  selector: '[appDynamicHeightVirtualScroll]',
  providers: [
    {
      provide: VIRTUAL_SCROLL_STRATEGY,
      useFactory: (directive: DynamicHeightVirtualScrollDirective) => directive.scrollStrategy,
      deps: [forwardRef(() => DynamicHeightVirtualScrollDirective)]
    }
  ],
  standalone: false
})
export class DynamicHeightVirtualScrollDirective implements AfterViewInit, OnDestroy {

  private readonly DEBOUNCE_MS = 100;
  private readonly scrollStrategy = new DynamicHeightVirtualScrollStrategy();
  private readonly resize$ = new ResizeObservable();
  private readonly ngUnsubscribe = new Subject<void>();

  private mutation$: MutationObservable;

  constructor(private elementRef: ElementRef) {
    let accumulatedHeightsByTicketId = new Map<string, number>;
    this.resize$.pipe(
      map((resizeObserverEntries: ResizeObserverEntry[]) => {
        return (resizeObserverEntries ?? []).reduce((heightById, element) => {
          const htmlElement = <HTMLElement> element?.target;
          if (htmlElement?.dataset?.id != null && htmlElement?.offsetHeight) {
            heightById.set(htmlElement.dataset.id, htmlElement.offsetHeight);
          }
          return heightById;

        }, accumulatedHeightsByTicketId);
      }),
      debounceTime(this.DEBOUNCE_MS),
      tap(() => accumulatedHeightsByTicketId = new Map()),
      filter(m => !!m?.size),
      takeUntil(this.ngUnsubscribe)
    ).subscribe(heightById => this.scrollStrategy.updateHeightCache(heightById));
  }

  ngAfterViewInit(): void {
    const virtualScrollWrapper: Element = this.elementRef.nativeElement.getElementsByClassName('cdk-virtual-scroll-content-wrapper')?.[0];
    if (virtualScrollWrapper != null) {
      this.mutation$ = new MutationObservable(virtualScrollWrapper, {childList: true, subtree: false});
      this.mutation$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(mutations => this.processAddedRemovedItems(mutations));
    } else {
      throw new Error('Cannot find Virtual Scroll Wrapper Element');
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  @Input()
  set items(items: Array<any>) {
    this.scrollStrategy.updateItems(items);
  }

  @Input()
  set defaultItemHeightPx(defaultHeight: number) {
    this.scrollStrategy.setDefaultItemHeight(defaultHeight);
  }


  /**
   * Number of pixels between items
   */
  @Input()
  set marginPx(margin: number) {
    this.scrollStrategy.setMargin(margin);
  }

  /**
   * Number of items to render above and below the viewport
   */
  @Input()
  set itemsPadding(n: number) {
    this.scrollStrategy.setItemPadding(n);
  }

  /**
   * Function that given an item returns its unique id
   */
  @Input()
  set trackBy(trackBy: (item: any) => string) {
    this.scrollStrategy.setTrackBy(trackBy);
  }

  private processAddedRemovedItems(mutations: MutationRecord[]): void {
    mutations?.forEach(mutation => {
      mutation.addedNodes?.forEach((node: Node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          const element = <HTMLElement> node;
          if (element.dataset.id != null) {
            this.resize$.registerElement(element);
          }
        }
      });

      mutation.removedNodes?.forEach((node: Node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          const element = <HTMLElement> node;
          if (element.dataset.id != null) {
            this.resize$.unregisterElement(element);
          }
        }
      });
    })
  }
}
